import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onCloseClick);
    this.addEventListener('toggle', this.#onAccordionToggle, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onCloseClick);
    this.removeEventListener('toggle', this.#onAccordionToggle, true);
  }

  /**
   * Close only from the X. Backdrop and category taps must not close the drawer.
   * @param {Event} event
   */
  #onCloseClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.menu-drawer__close-button')) return;
    event.preventDefault();
    event.stopPropagation();
    this.#close(this.refs.details);
  };

  /**
   * Keep one drawer accordion open at a time.
   * @param {Event} event
   */
  #onAccordionToggle = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLDetailsElement) || !target.open) return;
    if (target === this.refs.details) return;
    event.stopPropagation();

    const item = target.closest('li');
    const list = item?.parentElement;
    if (!list) return;

    list.querySelectorAll(':scope > li details[open]').forEach((details) => {
      if (details !== target) details.open = false;
    });
  };

  /**
   * True when the event started on a drawer control that should close the menu.
   * @param {Event} [event]
   */
  #isExplicitCloseEvent(event) {
    const origin = typeof event?.composedPath === 'function' ? event.composedPath()[0] : event?.target;
    if (!(origin instanceof Element)) return !event;
    if (origin.closest('.menu-drawer__close-button')) return true;
    if (origin.closest('.header__icon--menu, .header__icon--summary')) return true;
    if (origin.closest('.menu-drawer')) return false;
    return !!origin.closest('.menu-drawer__backdrop');
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (this.isOpen && event && !this.#isExplicitCloseEvent(event)) return;
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    if (details === this.refs.details) {
      document.body.classList.add('menu-open');
    }
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   * @param {Event} [event]
   */
  close(event) {
    if (event && !this.#isExplicitCloseEvent(event)) return;
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      removeTrapFocus();
      document.body.classList.remove('menu-open');

      const drawer = details.querySelector('.menu-drawer');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const finish = () => {
        if (!details.hasAttribute('open')) return;
        reset(details);
        this.querySelectorAll('details[open]').forEach(reset);
      };

      if (drawer && !reduceMotion) {
        let done = false;
        const once = () => {
          if (done) return;
          done = true;
          drawer.removeEventListener('transitionend', onEnd);
          finish();
        };
        const onEnd = (event) => {
          if (event.target !== drawer) return;
          once();
        };
        drawer.addEventListener('transitionend', onEnd);
        window.setTimeout(once, 280);
      } else {
        finish();
      }
      return;
    }

    onAnimationEnd(details, () => {
      reset(details);
      trapFocus(this.refs.details);
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
